import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/api";
import { Nav } from "@/components/ui/Nav";
import { Footer } from "@/components/ui/Footer";
import { BorderBeam } from "@/components/ui/border-beam";

type FormState = {
  nome: string;
  email: string;
  telefone: string;
  rating: number;
  comentario: string;
  honeypot: string;
};

const initialForm: FormState = {
  nome: "",
  email: "",
  telefone: "",
  rating: 0,
  comentario: "",
  honeypot: "",
};

function ratingLabel(rating: number): string {
  if (rating >= 5) return "Excelente";
  if (rating === 4) return "Boa";
  if (rating === 3) return "Neutra";
  if (rating === 2) return "Ruim";
  if (rating === 1) return "Muito ruim";
  return "Escolha uma nota";
}

function validateForm(form: FormState): string | null {
  if (form.nome.trim().length < 2) return "Preencha os campos obrigatorios: nome.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "Preencha os campos obrigatorios: e-mail valido.";
  if (form.telefone.replace(/\D/g, "").length < 10) return "Preencha os campos obrigatorios: telefone.";
  if (form.rating < 1) return "Preencha os campos obrigatorios: avaliacao da palestra.";
  return null;
}

export default function Palestra() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialForm);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goHome = () => {
    window.location.href = "https://udischool.com.br";
  };

  const goDiagnostic = () => {
    navigate("/agendar");
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      await apiFetch("/lecture-feedback", {
        method: "POST",
        body: {
          nome: form.nome,
          email: form.email,
          telefone: form.telefone,
          rating: form.rating,
          comentario: form.comentario || null,
          honeypot: form.honeypot || null,
        },
      });
      setSent(true);
    } catch {
      setError("Nao conseguimos enviar agora. Confira os campos e tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="palestra-page page-enter">
      <Nav onCTA={goDiagnostic} onHome={() => navigate("/")} />
      <main>
        <section className="palestra-hero">
          <div className="wrap palestra-wrap">
            <div className="palestra-copy">
              <span className="eyebrow">Palestra UdiSchool</span>
              <h1>Como foi sua experiencia?</h1>
              <p className="lead">
                Sua avaliacao ajuda a deixar os proximos encontros mais claros, uteis e proximos da vida real.
              </p>
            </div>

            <div className="palestra-card" aria-live="polite">
              {sent ? (
                <div className="palestra-thanks">
                  <span className="palestra-mark">✓</span>
                  <h2>Enviado. Obrigado pela avaliacao!</h2>
                  <p>
                    A UdiSchool nasceu para ajudar alunos de Uberlandia a destravar o ingles com direcao,
                    metodo e acompanhamento de verdade.
                  </p>
                  <p>
                    Tem interesse em estudar ingles com a gente? Conheca a UdiSchool e faca sua avaliacao diagnostica.
                  </p>
                  <button className="btn btn-primary btn-lg btn-beam btn-beam-primary" type="button" onClick={goHome}>
                    <BorderBeam />
                    Ir para udischool.com.br <span className="arr">→</span>
                  </button>
                </div>
              ) : (
                <form className="palestra-form" onSubmit={submit} noValidate>
                  <input
                    className="palestra-hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.honeypot}
                    onChange={(e) => update("honeypot", e.target.value)}
                    aria-hidden="true"
                  />

                  <label className="palestra-field">
                    <span className="palestra-label">Nome <span className="palestra-required" aria-hidden="true">*</span></span>
                    <input
                      value={form.nome}
                      onChange={(e) => update("nome", e.target.value)}
                      autoComplete="name"
                      required
                      minLength={2}
                      maxLength={160}
                      placeholder="Seu nome"
                    />
                  </label>

                  <div className="palestra-grid">
                    <label className="palestra-field">
                      <span className="palestra-label">E-mail <span className="palestra-required" aria-hidden="true">*</span></span>
                      <input
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        autoComplete="email"
                        required
                        type="email"
                        maxLength={190}
                        placeholder="voce@email.com"
                      />
                    </label>
                    <label className="palestra-field">
                      <span className="palestra-label">Telefone <span className="palestra-required" aria-hidden="true">*</span></span>
                      <input
                        value={form.telefone}
                        onChange={(e) => update("telefone", e.target.value)}
                        autoComplete="tel"
                        required
                        inputMode="tel"
                        minLength={8}
                        maxLength={40}
                        placeholder="(34) 99999-9999"
                      />
                    </label>
                  </div>

                  <fieldset className="palestra-rating">
                    <legend>Avaliacao da palestra <span className="palestra-required" aria-hidden="true">*</span></legend>
                    <div className="palestra-stars" role="radiogroup" aria-label="Avaliacao de 1 a 5 estrelas">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className={star <= form.rating ? "active" : ""}
                          role="radio"
                          aria-checked={form.rating === star}
                          aria-label={`${star} estrela${star > 1 ? "s" : ""}`}
                          onClick={() => update("rating", star)}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                    <span className="palestra-rating-label">{ratingLabel(form.rating)}</span>
                  </fieldset>

                  <label className="palestra-field">
                    <span className="palestra-label">Comentario opcional</span>
                    <textarea
                      value={form.comentario}
                      onChange={(e) => update("comentario", e.target.value)}
                      maxLength={4000}
                      placeholder="Conte o que mais ajudou ou o que podemos melhorar."
                    />
                  </label>

                  {error && <div className="palestra-error" role="alert">{error}</div>}

                  <button
                    className="btn btn-primary btn-lg btn-beam btn-beam-primary palestra-submit"
                    type="submit"
                    disabled={sending}
                  >
                    <BorderBeam />
                    {sending ? "Enviando..." : "Finalizar"} <span className="arr">→</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer onCTA={goDiagnostic} onHome={() => navigate("/")} />
    </div>
  );
}
